"use client"

import { useState, useRef, useEffect, useContext } from "react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Loader2, AlertCircle } from "lucide-react"
import type { NewReviewForm } from "@/types/review"
import { companySizeKeys, sectorKeys } from "@/constants/mock-data"
import { useTranslations } from "@/hooks/use-translations"
import { Checkbox } from "@/components/ui/checkbox"
import { ShadowContext } from "./user-review-shadow-root"

interface ReviewFormProps {
  productName: string
  onSubmit: (reviewData: NewReviewForm) => Promise<void>
  onCancel: () => void
}

const initialFormState: NewReviewForm = {
  name: "",
  role: "",
  company: "",
  companySize: "",
  sector: "",
  rating: 5,
  title: "",
  fullReview: "",
  pros: "",
  cons: "",
  userType: "",
  email: "",
  phone: "",
  acceptTerms: false,
}

// Email validation regex
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const MIN_FULL_REVIEW_LENGTH = 200 // Minimum characters to fill approximately 4 lines

export function ReviewForm({ productName, onSubmit, onCancel }: ReviewFormProps) {
  const [formData, setFormData] = useState<NewReviewForm>(initialFormState)
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Set<string>>(new Set())
  const [isLoggedIn, setIsLoggedIn] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const { t } = useTranslations()
  const shadowRoot = useContext(ShadowContext);

  // Check if user is logged in
  useEffect(() => {
    const checkLoginStatus = () => {
      if (typeof window !== "undefined" && window.ictportal_user_reviews?.logged_in) {
        setIsLoggedIn(true)
      }
    }

    checkLoginStatus()
  }, [])

  const validateEmail = (email: string): boolean => {
    return EMAIL_REGEX.test(email.trim())
  }

  const handleSubmit = async () => {
    try {
      setSubmitting(true)
      setSubmitError(null)

      // Define required fields based on login status
      const requiredFieldsMap: Record<string, string> = {
        name: t("name"), // Name is always required
        role: t("role"),
        companySize: t("companySize"),
        sector: t("sector"),
        title: t("title"),
        fullReview: t("fullReview"),
        pros: t("pros"),
        cons: t("cons"),
        userType: t("userType"),
        phone: t("phone"),
      }

      // Add email to required fields only if user is not logged in
      if (!isLoggedIn) {
        requiredFieldsMap.email = t("email")
      }

      const missingFields = Object.keys(requiredFieldsMap).filter((field) => {
        const value = formData[field as keyof typeof formData]
        return !value || (typeof value === "string" && value.trim() === "")
      })

      // Check for missing required fields
      if (missingFields.length > 0) {
        const missingLabels = missingFields.map((field) => requiredFieldsMap[field as keyof typeof requiredFieldsMap])
        setSubmitError(`${t("fillRequiredFields")} ${missingLabels.join(", ")}`)
        setFieldErrors(new Set(missingFields))

        // Scroll to top of modal to show error
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        return
      }

      // Validate email format only if user is not logged in and email is provided
      if (!isLoggedIn && formData.email && !validateEmail(formData.email)) {
        setSubmitError(t("invalidEmailFormat"))
        setFieldErrors(new Set(["email"]))
        return
      }

      // Validate minimum length for fullReview
      if (formData.fullReview.trim().length < MIN_FULL_REVIEW_LENGTH) {
        setSubmitError(t("fullReviewMinLength", { minLength: MIN_FULL_REVIEW_LENGTH }))
        setFieldErrors(new Set(["fullReview"]))
        if (formRef.current) {
          formRef.current.scrollIntoView({ behavior: "smooth", block: "start" })
        }
        return
      }

      // Check if terms are accepted only if user is not logged in
      if (!isLoggedIn && !formData.acceptTerms) {
        setSubmitError(t("acceptTermsRequired"))
        setFieldErrors(new Set(["acceptTerms"]))
        return
      }

      // Clear field errors if validation passes
      setFieldErrors(new Set())

      await onSubmit(formData)

      // Reset form on success
      setFormData(initialFormState)
    } catch (err) {
      setSubmitError(t("networkError"))
      console.error("Error submitting review:", err)
      throw err // Re-throw to let the parent handle it
    } finally {
      setSubmitting(false)
    }
  }

  const updateField = (field: keyof NewReviewForm, value: string | number | boolean) => {
    setFormData((prev) => ({ ...prev, [field]: value }))

    // Remove field from errors when user starts typing
    if (fieldErrors.has(field)) {
      const newErrors = new Set(fieldErrors)
      newErrors.delete(field)
      setFieldErrors(newErrors)
    }

    // Real-time email validation feedback only if user is not logged in
    /*    if (!isLoggedIn && field === "email" && typeof value === "string") {
         const newErrors = new Set(fieldErrors)
         if (value.trim() && !validateEmail(value)) {
           newErrors.add("email")
         } else {
           newErrors.delete("email")
         }
         setFieldErrors(newErrors)
       } */
  }

  const getFieldClassName = (fieldName: string, baseClassName = "") => {
    const hasError = fieldErrors.has(fieldName)
    return `${baseClassName} ${hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500" : ""}`.trim()
  }

  const fullReviewCharCount = formData.fullReview.length
  const isFullReviewBelowMin = fullReviewCharCount < MIN_FULL_REVIEW_LENGTH

  return (
    <div ref={formRef} className="space-y-4">
      {submitError && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{submitError}</AlertDescription>
        </Alert>
      )}

      {/* New introductory text */}
      <div className="text-sm text-gray-600 mb-4" dangerouslySetInnerHTML={{ __html: t("anonymousReviewInfo") }} />

      {/* Personal information section */}
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          {/* Name field is always visible */}
          <div>
            <Label htmlFor="name">
              {t("name")} {t("required")}
            </Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => updateField("name", e.target.value)}
              className={getFieldClassName("name")}
            />
          </div>
          {/* Role field */}
          <div>
            <Label htmlFor="role">
              {t("role")} {t("required")}
            </Label>
            <Input
              id="role"
              value={formData.role}
              onChange={(e) => updateField("role", e.target.value)}
              className={getFieldClassName("role")}
            />
          </div>
        </div>
      </div>

      {/* Company size and sector information section */}
      <div className="space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="sector">
              {t("sector")} {t("required")}
            </Label>
            <Select value={formData.sector} onValueChange={(value) => updateField("sector", value)}>
              <SelectTrigger className={getFieldClassName("sector")}>
                <SelectValue placeholder={t("selectSector")} />
              </SelectTrigger>
              <SelectContent container={shadowRoot || undefined}>
                {sectorKeys.map((sectorKey) => (
                  <SelectItem key={sectorKey} value={t(sectorKey as keyof typeof t)}>
                    <span className="md:text-[13px]">{t(sectorKey as keyof typeof t)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="companySize">
              {t("companySize")} {t("required")}
            </Label>
            <Select value={formData.companySize} onValueChange={(value) => updateField("companySize", value)}>
              <SelectTrigger className={getFieldClassName("companySize")}>
                <SelectValue placeholder={t("selectSize")} />
              </SelectTrigger>
              <SelectContent container={shadowRoot || undefined}>
                {companySizeKeys.map((sizeKey) => (
                  <SelectItem key={sizeKey} value={t(sizeKey as keyof typeof t)}>
                    <span className="md:text-[13px]">{t(sizeKey as keyof typeof t)}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Review information section */}
      <div className="space-y-4  border-gray-200">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="rating">
              {t("rating")} {t("required")}
            </Label>
            <Select
              value={formData.rating.toString()}
              onValueChange={(value) => updateField("rating", Number.parseInt(value))}
            >
              <SelectTrigger className={getFieldClassName("rating")}>
                <SelectValue placeholder={t("selectRating")} />
              </SelectTrigger>
              <SelectContent container={shadowRoot || undefined}>
                <SelectItem value="5">{t("excellent")}</SelectItem>
                <SelectItem value="4">{t("good")}</SelectItem>
                <SelectItem value="3">{t("average")}</SelectItem>
                <SelectItem value="2">{t("poor")}</SelectItem>
                <SelectItem value="1">{t("bad")}</SelectItem>
              </SelectContent >
            </Select>
          </div>
          <div>
            <Label htmlFor="userType">
              {t("userType")} {t("required")}
            </Label>
            <Select value={formData.userType} onValueChange={(value) => updateField("userType", value)}>
              <SelectTrigger className={getFieldClassName("userType")}>
                <SelectValue placeholder={t("selectStatus")} />
              </SelectTrigger>
              <SelectContent container={shadowRoot || undefined}>
                <SelectItem value={t("currentUser")}>{t("currentUser")}</SelectItem>
                <SelectItem value={t("formerUser")}>{t("formerUser")}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="title">
            {t("title")} {t("required")}
          </Label>
          <Input
            id="title"
            value={formData.title}
            onChange={(e) => updateField("title", e.target.value)}
            className={getFieldClassName("title")}
          />
        </div>

        <div>
          <Label htmlFor="fullReview" className="flex justify-between gap-1 mb-1">
            {t("fullReview")} {t("required")} <span className="text-gray-500">
              {t("characterCount", { current: fullReviewCharCount, min: MIN_FULL_REVIEW_LENGTH })}
            </span>
          </Label>
          <Textarea
            id="fullReview"
            value={formData.fullReview}
            onChange={(e) => updateField("fullReview", e.target.value)}
            placeholder={t("describeExperience")}
            rows={3}
            minLength={MIN_FULL_REVIEW_LENGTH} // Added minLength attribute
            className={getFieldClassName("fullReview")}
          />
          <div className="flex justify-between text-sm mt-1">
            {fieldErrors.has("fullReview") && formData.fullReview.trim().length > 0 && (
              <span className="text-red-600">{t("fullReviewMinLength", { minLength: MIN_FULL_REVIEW_LENGTH })}</span>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="pros">
              {t("pros")} {t("required")}
            </Label>
            <Textarea
              id="pros"
              value={formData.pros}
              onChange={(e) => updateField("pros", e.target.value)}
              placeholder={t("strongPoints")}
              rows={2}
              className={getFieldClassName("pros", "min-h-[60px]")}
            />
          </div>
          <div>
            <Label htmlFor="cons">
              {t("cons")} {t("required")}
            </Label>
            <Textarea
              id="cons"
              value={formData.cons}
              onChange={(e) => updateField("cons", e.target.value)}
              placeholder={t("couldBeBetter")}
              rows={2}
              className={getFieldClassName("cons", "min-h-[60px]")}
            />
          </div>
        </div>
      </div>

      {/* Contact information section */}
      <div className="space-y-4  border-gray-200">
        <p className="text-sm text-gray-600">{t("contactInfoValidationText")}</p>
        <div className="grid md:grid-cols-2 gap-4">
          <div className={!isLoggedIn ? "md:col-span-2" : ""}>
            <Label htmlFor="company">{t("companyOptional")}</Label> {/* Updated label */}
            <Input
              id="company"
              value={formData.company}
              onChange={(e) => updateField("company", e.target.value)}
              className={getFieldClassName("company")}
            />
          </div>
          {/* Email field is only visible if not logged in */}
          {!isLoggedIn && (
            <div>
              <Label htmlFor="email">
                {t("email")} {t("required")}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => updateField("email", e.target.value)}
                className={getFieldClassName("email")}
              />
              {fieldErrors.has("email") && formData.email.trim() && (
                <p className="text-sm text-red-600 mt-1">{t("invalidEmailFormat")}</p>
              )}
            </div>
          )}
          {/* Phone field is always visible */}
          <div>
            {" "}
            {/* Adjust span based on email visibility */}
            <Label htmlFor="phone">
              {t("phone")} {t("required")}
            </Label>
            <Input
              id="phone"
              type="tel"
              value={formData.phone}
              onChange={(e) => updateField("phone", e.target.value)}
              className={getFieldClassName("phone")}
            />
          </div>
        </div>
      </div>

      {/* Terms and conditions section - only show if not logged in */}
      {!isLoggedIn && (
        <div className="space-y-4  border-gray-200">
          <div className="flex items-start space-x-2">
            <Checkbox
              id="acceptTerms"
              checked={formData.acceptTerms}
              onCheckedChange={(checked) => updateField("acceptTerms", checked === true)}
              className={fieldErrors.has("acceptTerms") ? "border-red-500" : ""}
            />
            <Label
              htmlFor="acceptTerms"
              className={`text-sm leading-5 cursor-pointer ${fieldErrors.has("acceptTerms") ? "text-red-600" : ""}`}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: t("acceptTermsAndPrivacy")
                    .replace(
                      "{termsLink}",
                      `<a href="${t("termsUrl")}" target="_blank" class="text-blue-600 hover:underline">${t("termsOfUse")}</a>`,
                    )
                    .replace(
                      "{privacyLink}",
                      `<a href="${t("privacyUrl")}" target="_blank" class="text-blue-600 hover:underline">${t("privacyPolicy")}</a>`,
                    ),
                }}
              />
              <span className="ml-1">{t("required")}</span>
            </Label>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 ">
        <Button variant="outline" onClick={onCancel} disabled={submitting}>
          {t("cancel")}
        </Button>
        <Button onClick={handleSubmit} disabled={submitting}>
          {submitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              {t("submitting")}
            </>
          ) : (
            t("publishReview")
          )}
        </Button>
      </div>
    </div>
  )
}
